/**
 * Materials Knowledge Base - Comprehensive Database
 * Based on real material datasheets and industry standards
 * Created: 2025-01-09
 */

// Typy dla właściwości materiałów
export interface MechanicalProperties {
  tensileStrength: number; // MPa
  tensileModulus: number; // GPa (Young's modulus)
  flexuralStrength: number; // MPa
  flexuralModulus: number; // GPa
  compressiveStrength: number; // MPa
  impactStrength: number; // kJ/m² (Charpy)
  hardness: string; // Rockwell scale
  elongationAtBreak: number; // %
  shearStrength: number; // MPa
  poissonRatio: number;
}

export interface ThermalProperties {
  meltingPoint: number; // °C
  glassTransition: number; // °C (Tg)
  heatDeflection: number; // °C at 0.45 MPa
  maxServiceTemp: number; // °C continuous
  thermalExpansion: number; // µm/m/°C
  thermalConductivity: number; // W/m·K
  specificHeat: number; // J/g·°C
  flammability: string; // UL94 rating
}

export interface OpticalProperties {
  transparency: number; // % light transmission
  refractiveIndex: number;
  haze: number; // %
  uvResistance: 'poor' | 'fair' | 'good' | 'excellent';
  yellowingResistance: 'poor' | 'fair' | 'good' | 'excellent';
}

export interface ChemicalProperties {
  waterAbsorption: number; // % at 24h
  chemicalResistance: {
    acids: 'poor' | 'fair' | 'good' | 'excellent';
    bases: 'poor' | 'fair' | 'good' | 'excellent';
    solvents: 'poor' | 'fair' | 'good' | 'excellent';
    oils: 'poor' | 'fair' | 'good' | 'excellent';
    alcohols: 'poor' | 'fair' | 'good' | 'excellent';
  };
  weatherResistance: 'poor' | 'fair' | 'good' | 'excellent';
}

export interface ElectricalProperties {
  dielectricStrength: number; // kV/mm
  dielectricConstant: number; // at 1 MHz
  volumeResistivity: number; // Ω·cm
  surfaceResistivity: number; // Ω
  arcResistance: number; // seconds
}

export interface ProcessingProperties {
  moldingTemp: { min: number; max: number }; // °C
  moldTemp: { min: number; max: number }; // °C
  dryingTemp: number; // °C
  dryingTime: number; // hours
  shrinkage: number; // %
  machinability: 'poor' | 'fair' | 'good' | 'excellent';
  weldability: 'poor' | 'fair' | 'good' | 'excellent';
  bondability: 'poor' | 'fair' | 'good' | 'excellent';
}

export interface Material {
  id: string;
  name: string;
  category: 'thermoplastic' | 'thermoset' | 'composite' | 'metal' | 'glass';
  tradenames: string[];
  density: number; // g/cm³
  price: number; // PLN/kg (orientacyjna)
  mechanical: MechanicalProperties;
  thermal: ThermalProperties;
  optical: OpticalProperties;
  chemical: ChemicalProperties;
  electrical: ElectricalProperties;
  processing: ProcessingProperties;
  applications: string[];
  advantages: string[];
  disadvantages: string[];
  sustainability: {
    recyclable: boolean;
    recyclingCode?: number;
    biodegradable: boolean;
    carbonFootprint: number; // kg CO2/kg
  };
}

// BAZA DANYCH MATERIAŁÓW - rzeczywiste dane z kart katalogowych
export const MATERIALS_DATABASE: Material[] = [
  {
    id: 'pmma-cast',
    name: 'PMMA odlewane (Cast Acrylic)',
    category: 'thermoplastic',
    tradenames: ['Plexiglas®', 'Acrylite®', 'Lucite®', 'Perspex®', 'Altuglas®'],
    density: 1.19,
    price: 18,
    mechanical: {
      tensileStrength: 72, // MPa (rzeczywiste dane)
      tensileModulus: 3.3, // GPa
      flexuralStrength: 115,
      flexuralModulus: 3.2,
      compressiveStrength: 120,
      impactStrength: 15, // kJ/m² (Charpy)
      hardness: 'M92-M100',
      elongationAtBreak: 4.5,
      shearStrength: 75,
      poissonRatio: 0.37
    },
    thermal: {
      meltingPoint: 160,
      glassTransition: 105,
      heatDeflection: 95,
      maxServiceTemp: 80,
      thermalExpansion: 70,
      thermalConductivity: 0.19,
      specificHeat: 1.47,
      flammability: 'HB'
    },
    optical: {
      transparency: 92,
      refractiveIndex: 1.49,
      haze: 1,
      uvResistance: 'good',
      yellowingResistance: 'good'
    },
    chemical: {
      waterAbsorption: 0.3,
      chemicalResistance: {
        acids: 'good',
        bases: 'good',
        solvents: 'poor',
        oils: 'excellent',
        alcohols: 'fair'
      },
      weatherResistance: 'excellent'
    },
    electrical: {
      dielectricStrength: 20,
      dielectricConstant: 3.7,
      volumeResistivity: 1e15,
      surfaceResistivity: 1e14,
      arcResistance: 250
    },
    processing: {
      moldingTemp: { min: 240, max: 270 },
      moldTemp: { min: 40, max: 80 },
      dryingTemp: 80,
      dryingTime: 4,
      shrinkage: 0.4,
      machinability: 'excellent',
      weldability: 'poor',
      bondability: 'excellent'
    },
    applications: [
      'Szyby ochronne',
      'Ekspozytory reklamowe',
      'Akwaria',
      'Przegrody',
      'Osłony maszyn',
      'Elementy optyczne',
      'Meble designerskie'
    ],
    advantages: [
      'Doskonała przezroczystość (92%)',
      'Łatwa obróbka mechaniczna',
      'Odporność na UV i warunki atmosferyczne',
      'Możliwość polerowania',
      'Sztywność',
      'Dostępność w wielu kolorach'
    ],
    disadvantages: [
      'Kruchość',
      'Wrażliwość na rozpuszczalniki',
      'Palność',
      'Tendencja do pękania naprężeniowego',
      'Niższa odporność na uderzenia niż PC'
    ],
    sustainability: {
      recyclable: true,
      recyclingCode: 7,
      biodegradable: false,
      carbonFootprint: 5.5
    }
  },
  {
    id: 'pmma-extruded',
    name: 'PMMA ekstrudowane (Extruded Acrylic)',
    category: 'thermoplastic',
    tradenames: ['Plexiglas® XT', 'OPTIX®', 'ACRYLITE® FF'],
    density: 1.19,
    price: 14,
    mechanical: {
      tensileStrength: 65,
      tensileModulus: 3.0,
      flexuralStrength: 100,
      flexuralModulus: 2.9,
      compressiveStrength: 110,
      impactStrength: 12,
      hardness: 'M85-M95',
      elongationAtBreak: 3.5,
      shearStrength: 70,
      poissonRatio: 0.37
    },
    thermal: {
      meltingPoint: 150,
      glassTransition: 95,
      heatDeflection: 85,
      maxServiceTemp: 70,
      thermalExpansion: 75,
      thermalConductivity: 0.19,
      specificHeat: 1.47,
      flammability: 'HB'
    },
    optical: {
      transparency: 92,
      refractiveIndex: 1.49,
      haze: 2,
      uvResistance: 'fair',
      yellowingResistance: 'fair'
    },
    chemical: {
      waterAbsorption: 0.3,
      chemicalResistance: {
        acids: 'good',
        bases: 'good',
        solvents: 'poor',
        oils: 'excellent',
        alcohols: 'fair'
      },
      weatherResistance: 'good'
    },
    electrical: {
      dielectricStrength: 18,
      dielectricConstant: 3.7,
      volumeResistivity: 1e15,
      surfaceResistivity: 1e14,
      arcResistance: 240
    },
    processing: {
      moldingTemp: { min: 230, max: 260 },
      moldTemp: { min: 40, max: 70 },
      dryingTemp: 75,
      dryingTime: 3,
      shrinkage: 0.6,
      machinability: 'good',
      weldability: 'poor',
      bondability: 'excellent'
    },
    applications: [
      'Produkcja masowa',
      'Elementy termoformowane',
      'Wycinanie laserowe',
      'Znaki informacyjne',
      'Osłony LED'
    ],
    advantages: [
      'Niższy koszt niż PMMA odlewane',
      'Lepsze do termoformowania',
      'Mniejsze naprężenia wewnętrzne',
      'Łatwiejsze cięcie laserem'
    ],
    disadvantages: [
      'Nieco gorsza jakość optyczna',
      'Większa tolerancja grubości',
      'Mniejsza twardość powierzchni'
    ],
    sustainability: {
      recyclable: true,
      recyclingCode: 7,
      biodegradable: false,
      carbonFootprint: 4.8
    }
  },
  {
    id: 'pc',
    name: 'Poliwęglan (PC)',
    category: 'thermoplastic',
    tradenames: ['Lexan®', 'Makrolon®', 'Panlite®', 'Marlon®'],
    density: 1.20,
    price: 25,
    mechanical: {
      tensileStrength: 66, // MPa (dane z Lexan)
      tensileModulus: 2.4, // GPa
      flexuralStrength: 90,
      flexuralModulus: 2.3,
      compressiveStrength: 86,
      impactStrength: 850, // kJ/m² - wyjątkowa odporność!
      hardness: 'M70-M75 / R118-R122',
      elongationAtBreak: 110,
      shearStrength: 68,
      poissonRatio: 0.37
    },
    thermal: {
      meltingPoint: 267,
      glassTransition: 147,
      heatDeflection: 135,
      maxServiceTemp: 120,
      thermalExpansion: 65,
      thermalConductivity: 0.21,
      specificHeat: 1.26,
      flammability: 'V-2'
    },
    optical: {
      transparency: 88,
      refractiveIndex: 1.586,
      haze: 1,
      uvResistance: 'poor',
      yellowingResistance: 'poor'
    },
    chemical: {
      waterAbsorption: 0.15,
      chemicalResistance: {
        acids: 'good',
        bases: 'fair',
        solvents: 'poor',
        oils: 'good',
        alcohols: 'fair'
      },
      weatherResistance: 'fair'
    },
    electrical: {
      dielectricStrength: 30,
      dielectricConstant: 3.0,
      volumeResistivity: 2e16,
      surfaceResistivity: 1e15,
      arcResistance: 120
    },
    processing: {
      moldingTemp: { min: 280, max: 320 },
      moldTemp: { min: 80, max: 120 },
      dryingTemp: 120,
      dryingTime: 4,
      shrinkage: 0.7,
      machinability: 'good',
      weldability: 'good',
      bondability: 'good'
    },
    applications: [
      'Szyby kuloodporne',
      'Kaski i osłony ochronne',
      'Komponenty elektroniczne',
      'Nośniki optyczne (CD/DVD)',
      'Części samochodowe',
      'Przyrządy medyczne',
      'Butelki wielorazowe'
    ],
    advantages: [
      'Wyjątkowa odporność na uderzenia',
      'Wysoka temperatura pracy',
      'Dobra sztywność',
      'Możliwość sterylizacji',
      'Samogas   nący',
      'Możliwość formowania na zimno'
    ],
    disadvantages: [
      'Wrażliwość na UV (żółknięcie)',
      'Wrażliwość na rozpuszczalniki',
      'Tendencja do pękania naprężeniowego',
      'Wymaga suszenia przed przetwórstwem',
      'Wyższy koszt niż PMMA'
    ],
    sustainability: {
      recyclable: true,
      recyclingCode: 7,
      biodegradable: false,
      carbonFootprint: 7.6
    }
  },
  {
    id: 'petg',
    name: 'PET-G (glikol)',
    category: 'thermoplastic',
    tradenames: ['Vivak®', 'SPECTAR®', 'Eastar®'],
    density: 1.27,
    price: 16,
    mechanical: {
      tensileStrength: 50,
      tensileModulus: 2.0,
      flexuralStrength: 70,
      flexuralModulus: 2.1,
      compressiveStrength: 65,
      impactStrength: 85,
      hardness: 'R105-R110',
      elongationAtBreak: 180,
      shearStrength: 55,
      poissonRatio: 0.38
    },
    thermal: {
      meltingPoint: 245,
      glassTransition: 80,
      heatDeflection: 70,
      maxServiceTemp: 65,
      thermalExpansion: 68,
      thermalConductivity: 0.29,
      specificHeat: 1.0,
      flammability: 'HB'
    },
    optical: {
      transparency: 90,
      refractiveIndex: 1.57,
      haze: 1,
      uvResistance: 'good',
      yellowingResistance: 'good'
    },
    chemical: {
      waterAbsorption: 0.13,
      chemicalResistance: {
        acids: 'fair',
        bases: 'fair',
        solvents: 'good',
        oils: 'excellent',
        alcohols: 'good'
      },
      weatherResistance: 'good'
    },
    electrical: {
      dielectricStrength: 17,
      dielectricConstant: 3.4,
      volumeResistivity: 1e16,
      surfaceResistivity: 1e15,
      arcResistance: 110
    },
    processing: {
      moldingTemp: { min: 220, max: 250 },
      moldTemp: { min: 20, max: 40 },
      dryingTemp: 65,
      dryingTime: 2,
      shrinkage: 0.4,
      machinability: 'excellent',
      weldability: 'good',
      bondability: 'excellent'
    },
    applications: [
      'Osłony ochronne COVID',
      'Ekspozytory POS',
      'Opakowania',
      'Karty plastikowe',
      'Elementy medyczne',
      'Obudowy urządzeń'
    ],
    advantages: [
      'Doskonała odporność chemiczna',
      'Łatwe termoformowanie',
      'Dobra udarność',
      'Nie wymaga suszenia',
      'Możliwość recyklingu',
      'Przezroczystość'
    ],
    disadvantages: [
      'Niska temperatura pracy',
      'Mniejsza sztywność niż PMMA',
      'Tendencja do zarysowań'
    ],
    sustainability: {
      recyclable: true,
      recyclingCode: 1,
      biodegradable: false,
      carbonFootprint: 3.4
    }
  },
  {
    id: 'ps',
    name: 'Polistyren (PS)',
    category: 'thermoplastic',
    tradenames: ['Styron®', 'Lustrex®', 'Styrolux®'],
    density: 1.05,
    price: 8,
    mechanical: {
      tensileStrength: 45,
      tensileModulus: 3.0,
      flexuralStrength: 80,
      flexuralModulus: 3.1,
      compressiveStrength: 80,
      impactStrength: 2,
      hardness: 'M65-M75',
      elongationAtBreak: 3,
      shearStrength: 50,
      poissonRatio: 0.35
    },
    thermal: {
      meltingPoint: 240,
      glassTransition: 100,
      heatDeflection: 85,
      maxServiceTemp: 70,
      thermalExpansion: 80,
      thermalConductivity: 0.14,
      specificHeat: 1.3,
      flammability: 'HB'
    },
    optical: {
      transparency: 90,
      refractiveIndex: 1.59,
      haze: 2,
      uvResistance: 'poor',
      yellowingResistance: 'poor'
    },
    chemical: {
      waterAbsorption: 0.05,
      chemicalResistance: {
        acids: 'good',
        bases: 'good',
        solvents: 'poor',
        oils: 'poor',
        alcohols: 'good'
      },
      weatherResistance: 'poor'
    },
    electrical: {
      dielectricStrength: 20,
      dielectricConstant: 2.6,
      volumeResistivity: 1e16,
      surfaceResistivity: 1e15,
      arcResistance: 60
    },
    processing: {
      moldingTemp: { min: 180, max: 250 },
      moldTemp: { min: 20, max: 60 },
      dryingTemp: 80,
      dryingTime: 2,
      shrinkage: 0.5,
      machinability: 'fair',
      weldability: 'poor',
      bondability: 'good'
    },
    applications: [
      'Opakowania jednorazowe',
      'Zabawki',
      'Pojemniki na żywność',
      'Izolacje',
      'Modele'
    ],
    advantages: [
      'Niski koszt',
      'Łatwe przetwórstwo',
      'Przezroczystość',
      'Sztywność',
      'Dobra izolacja elektryczna'
    ],
    disadvantages: [
      'Kruchość',
      'Niska odporność na UV',
      'Wrażliwość na rozpuszczalniki',
      'Niska temperatura pracy'
    ],
    sustainability: {
      recyclable: true,
      recyclingCode: 6,
      biodegradable: false,
      carbonFootprint: 3.4
    }
  },
  {
    id: 'pvc-rigid',
    name: 'PVC sztywne',
    category: 'thermoplastic',
    tradenames: ['Trovidur®', 'Kömacel®', 'Forex®', 'Palram®'],
    density: 1.38,
    price: 10,
    mechanical: {
      tensileStrength: 50,
      tensileModulus: 3.0,
      flexuralStrength: 90,
      flexuralModulus: 2.9,
      compressiveStrength: 66,
      impactStrength: 3,
      hardness: 'R105-R115',
      elongationAtBreak: 40,
      shearStrength: 45,
      poissonRatio: 0.38
    },
    thermal: {
      meltingPoint: 212,
      glassTransition: 80,
      heatDeflection: 70,
      maxServiceTemp: 60,
      thermalExpansion: 80,
      thermalConductivity: 0.16,
      specificHeat: 0.9,
      flammability: 'V-0'
    },
    optical: {
      transparency: 85,
      refractiveIndex: 1.54,
      haze: 5,
      uvResistance: 'good',
      yellowingResistance: 'fair'
    },
    chemical: {
      waterAbsorption: 0.04,
      chemicalResistance: {
        acids: 'excellent',
        bases: 'excellent',
        solvents: 'fair',
        oils: 'good',
        alcohols: 'excellent'
      },
      weatherResistance: 'good'
    },
    electrical: {
      dielectricStrength: 14,
      dielectricConstant: 3.4,
      volumeResistivity: 1e14,
      surfaceResistivity: 1e13,
      arcResistance: 60
    },
    processing: {
      moldingTemp: { min: 170, max: 190 },
      moldTemp: { min: 20, max: 50 },
      dryingTemp: 60,
      dryingTime: 2,
      shrinkage: 0.5,
      machinability: 'good',
      weldability: 'excellent',
      bondability: 'excellent'
    },
    applications: [
      'Rury i profile',
      'Tablice reklamowe',
      'Okna i drzwi',
      'Zbiorniki chemiczne',
      'Karty plastikowe'
    ],
    advantages: [
      'Doskonała odporność chemiczna',
      'Samogas   nący',
      'Niski koszt',
      'Łatwe spawanie',
      'Dobra sztywność'
    ],
    disadvantages: [
      'Niska temperatura pracy',
      'Wrażliwość na temperaturę',
      'Uwalnianie HCl przy spalaniu',
      'Sztywność/kruchość'
    ],
    sustainability: {
      recyclable: true,
      recyclingCode: 3,
      biodegradable: false,
      carbonFootprint: 2.0
    }
  },
  {
    id: 'hdpe',
    name: 'Polietylen HD',
    category: 'thermoplastic',
    tradenames: ['Hostalen®', 'Lupolen®', 'Marlex®'],
    density: 0.96,
    price: 7,
    mechanical: {
      tensileStrength: 32,
      tensileModulus: 1.1,
      flexuralStrength: 30,
      flexuralModulus: 1.2,
      compressiveStrength: 20,
      impactStrength: 'no break',
      hardness: 'D60-D70',
      elongationAtBreak: 800,
      shearStrength: 25,
      poissonRatio: 0.46
    },
    thermal: {
      meltingPoint: 135,
      glassTransition: -120,
      heatDeflection: 75,
      maxServiceTemp: 80,
      thermalExpansion: 200,
      thermalConductivity: 0.48,
      specificHeat: 2.3,
      flammability: 'HB'
    },
    optical: {
      transparency: 0,
      refractiveIndex: 1.54,
      haze: 100,
      uvResistance: 'poor',
      yellowingResistance: 'good'
    },
    chemical: {
      waterAbsorption: 0.01,
      chemicalResistance: {
        acids: 'excellent',
        bases: 'excellent',
        solvents: 'good',
        oils: 'fair',
        alcohols: 'excellent'
      },
      weatherResistance: 'fair'
    },
    electrical: {
      dielectricStrength: 18,
      dielectricConstant: 2.3,
      volumeResistivity: 1e16,
      surfaceResistivity: 1e13,
      arcResistance: 135
    },
    processing: {
      moldingTemp: { min: 200, max: 280 },
      moldTemp: { min: 20, max: 60 },
      dryingTemp: 0,
      dryingTime: 0,
      shrinkage: 2.0,
      machinability: 'fair',
      weldability: 'excellent',
      bondability: 'poor'
    },
    applications: [
      'Zbiorniki na chemikalia',
      'Rury ciśnieniowe',
      'Deski do krojenia',
      'Pojemniki',
      'Folie'
    ],
    advantages: [
      'Doskonała odporność chemiczna',
      'Niski koszt',
      'Elastyczność',
      'Nie wymaga suszenia',
      'Odporność na pękanie naprężeniowe'
    ],
    disadvantages: [
      'Nieprzezroczysty',
      'Trudny do klejenia',
      'Duży skurcz',
      'Niska sztywność',
      'Niska temperatura pracy'
    ],
    sustainability: {
      recyclable: true,
      recyclingCode: 2,
      biodegradable: false,
      carbonFootprint: 1.9
    }
  },
  {
    id: 'pp',
    name: 'Polipropylen (PP)',
    category: 'thermoplastic',
    tradenames: ['Moplen®', 'Pro-fax®', 'Novolen®'],
    density: 0.91,
    price: 6,
    mechanical: {
      tensileStrength: 35,
      tensileModulus: 1.5,
      flexuralStrength: 45,
      flexuralModulus: 1.4,
      compressiveStrength: 40,
      impactStrength: 30,
      hardness: 'R80-R100',
      elongationAtBreak: 500,
      shearStrength: 30,
      poissonRatio: 0.42
    },
    thermal: {
      meltingPoint: 165,
      glassTransition: -10,
      heatDeflection: 100,
      maxServiceTemp: 100,
      thermalExpansion: 150,
      thermalConductivity: 0.22,
      specificHeat: 1.9,
      flammability: 'HB'
    },
    optical: {
      transparency: 0,
      refractiveIndex: 1.49,
      haze: 100,
      uvResistance: 'poor',
      yellowingResistance: 'good'
    },
    chemical: {
      waterAbsorption: 0.03,
      chemicalResistance: {
        acids: 'excellent',
        bases: 'excellent',
        solvents: 'good',
        oils: 'fair',
        alcohols: 'excellent'
      },
      weatherResistance: 'fair'
    },
    electrical: {
      dielectricStrength: 22,
      dielectricConstant: 2.2,
      volumeResistivity: 1e17,
      surfaceResistivity: 1e13,
      arcResistance: 138
    },
    processing: {
      moldingTemp: { min: 200, max: 280 },
      moldTemp: { min: 20, max: 80 },
      dryingTemp: 0,
      dryingTime: 0,
      shrinkage: 1.5,
      machinability: 'fair',
      weldability: 'excellent',
      bondability: 'poor'
    },
    applications: [
      'Części samochodowe',
      'Opakowania żywności',
      'Meble ogrodowe',
      'Zabawki',
      'Zbiorniki'
    ],
    advantages: [
      'Najniższy koszt',
      'Doskonała odporność chemiczna',
      'Wysoka temperatura pracy',
      'Elastyczność',
      'Możliwość sterylizacji'
    ],
    disadvantages: [
      'Nieprzezroczysty',
      'Trudny do klejenia',
      'Wrażliwość na UV',
      'Niska sztywność'
    ],
    sustainability: {
      recyclable: true,
      recyclingCode: 5,
      biodegradable: false,
      carbonFootprint: 1.7
    }
  },
  {
    id: 'pa6',
    name: 'Poliamid 6 (Nylon 6)',
    category: 'thermoplastic',
    tradenames: ['Akulon®', 'Durethan®', 'Zytel®'],
    density: 1.14,
    price: 20,
    mechanical: {
      tensileStrength: 85,
      tensileModulus: 3.2,
      flexuralStrength: 110,
      flexuralModulus: 2.8,
      compressiveStrength: 90,
      impactStrength: 50,
      hardness: 'R120 / M80',
      elongationAtBreak: 60,
      shearStrength: 65,
      poissonRatio: 0.39
    },
    thermal: {
      meltingPoint: 220,
      glassTransition: 50,
      heatDeflection: 180,
      maxServiceTemp: 100,
      thermalExpansion: 80,
      thermalConductivity: 0.25,
      specificHeat: 1.7,
      flammability: 'V-2'
    },
    optical: {
      transparency: 0,
      refractiveIndex: 1.53,
      haze: 100,
      uvResistance: 'fair',
      yellowingResistance: 'fair'
    },
    chemical: {
      waterAbsorption: 2.8,
      chemicalResistance: {
        acids: 'poor',
        bases: 'good',
        solvents: 'excellent',
        oils: 'excellent',
        alcohols: 'good'
      },
      weatherResistance: 'fair'
    },
    electrical: {
      dielectricStrength: 20,
      dielectricConstant: 4.0,
      volumeResistivity: 1e13,
      surfaceResistivity: 1e13,
      arcResistance: 120
    },
    processing: {
      moldingTemp: { min: 230, max: 280 },
      moldTemp: { min: 40, max: 90 },
      dryingTemp: 80,
      dryingTime: 4,
      shrinkage: 1.2,
      machinability: 'excellent',
      weldability: 'good',
      bondability: 'good'
    },
    applications: [
      'Łożyska',
      'Koła zębate',
      'Prowadnice',
      'Rolki',
      'Części mechaniczne'
    ],
    advantages: [
      'Wysoka wytrzymałość',
      'Doskonała odporność na ścieranie',
      'Samosmarność',
      'Sztywność',
      'Odporność na oleje'
    ],
    disadvantages: [
      'Wysoka absorpcja wilgoci',
      'Wymaga suszenia',
      'Wrażliwość na kwasy',
      'Zmiana wymiarów przy nawilżeniu'
    ],
    sustainability: {
      recyclable: true,
      recyclingCode: 7,
      biodegradable: false,
      carbonFootprint: 7.9
    }
  },
  {
    id: 'pom',
    name: 'Polioksymetylen (POM)',
    category: 'thermoplastic',
    tradenames: ['Delrin®', 'Hostaform®', 'Ultraform®'],
    density: 1.42,
    price: 25,
    mechanical: {
      tensileStrength: 70,
      tensileModulus: 3.1,
      flexuralStrength: 95,
      flexuralModulus: 2.8,
      compressiveStrength: 110,
      impactStrength: 80,
      hardness: 'R120 / M85',
      elongationAtBreak: 25,
      shearStrength: 60,
      poissonRatio: 0.37
    },
    thermal: {
      meltingPoint: 175,
      glassTransition: -60,
      heatDeflection: 160,
      maxServiceTemp: 100,
      thermalExpansion: 110,
      thermalConductivity: 0.31,
      specificHeat: 1.5,
      flammability: 'HB'
    },
    optical: {
      transparency: 0,
      refractiveIndex: 1.48,
      haze: 100,
      uvResistance: 'poor',
      yellowingResistance: 'good'
    },
    chemical: {
      waterAbsorption: 0.2,
      chemicalResistance: {
        acids: 'poor',
        bases: 'fair',
        solvents: 'excellent',
        oils: 'excellent',
        alcohols: 'excellent'
      },
      weatherResistance: 'fair'
    },
    electrical: {
      dielectricStrength: 19,
      dielectricConstant: 3.8,
      volumeResistivity: 1e14,
      surfaceResistivity: 1e13,
      arcResistance: 220
    },
    processing: {
      moldingTemp: { min: 180, max: 230 },
      moldTemp: { min: 60, max: 120 },
      dryingTemp: 80,
      dryingTime: 2,
      shrinkage: 2.0,
      machinability: 'excellent',
      weldability: 'fair',
      bondability: 'fair'
    },
    applications: [
      'Koła zębate precyzyjne',
      'Łożyska',
      'Zawory',
      'Pompy',
      'Części precyzyjne'
    ],
    advantages: [
      'Wysoka sztywność',
      'Doskonała stabilność wymiarowa',
      'Niski współczynnik tarcia',
      'Odporność na zmęczenie',
      'Dobra sprężystość'
    ],
    disadvantages: [
      'Wrażliwość na kwasy',
      'Trudny do klejenia',
      'Wrażliwość na UV',
      'Palność'
    ],
    sustainability: {
      recyclable: true,
      recyclingCode: 7,
      biodegradable: false,
      carbonFootprint: 4.5
    }
  },
  {
    id: 'ptfe',
    name: 'Politetrafluoroetylen (PTFE)',
    category: 'thermoplastic',
    tradenames: ['Teflon®', 'Fluon®', 'Algoflon®'],
    density: 2.17,
    price: 80,
    mechanical: {
      tensileStrength: 30,
      tensileModulus: 0.5,
      flexuralStrength: 0, // nie pęka, tylko odkształca
      flexuralModulus: 0.6,
      compressiveStrength: 12,
      impactStrength: 160,
      hardness: 'D50-D60',
      elongationAtBreak: 350,
      shearStrength: 5,
      poissonRatio: 0.46
    },
    thermal: {
      meltingPoint: 327,
      glassTransition: -97,
      heatDeflection: 55,
      maxServiceTemp: 260,
      thermalExpansion: 135,
      thermalConductivity: 0.25,
      specificHeat: 1.0,
      flammability: 'V-0'
    },
    optical: {
      transparency: 0,
      refractiveIndex: 1.38,
      haze: 100,
      uvResistance: 'excellent',
      yellowingResistance: 'excellent'
    },
    chemical: {
      waterAbsorption: 0.01,
      chemicalResistance: {
        acids: 'excellent',
        bases: 'excellent',
        solvents: 'excellent',
        oils: 'excellent',
        alcohols: 'excellent'
      },
      weatherResistance: 'excellent'
    },
    electrical: {
      dielectricStrength: 60,
      dielectricConstant: 2.1,
      volumeResistivity: 1e18,
      surfaceResistivity: 1e17,
      arcResistance: 300
    },
    processing: {
      moldingTemp: { min: 360, max: 380 },
      moldTemp: { min: 150, max: 200 },
      dryingTemp: 150,
      dryingTime: 4,
      shrinkage: 3.5,
      machinability: 'good',
      weldability: 'poor',
      bondability: 'poor'
    },
    applications: [
      'Uszczelki chemiczne',
      'Łożyska bezsmarowe',
      'Izolacja elektryczna',
      'Powłoki nieprzywierające',
      'Membrany'
    ],
    advantages: [
      'Najlepsza odporność chemiczna',
      'Najniższy współczynnik tarcia',
      'Szeroki zakres temperatur',
      'Niepalność',
      'Doskonała izolacja elektryczna'
    ],
    disadvantages: [
      'Wysoki koszt',
      'Niska wytrzymałość mechaniczna',
      'Trudna obróbka',
      'Pełzanie pod obciążeniem',
      'Nie nadaje się do klejenia'
    ],
    sustainability: {
      recyclable: false,
      biodegradable: false,
      carbonFootprint: 12.0
    }
  }
];

// Funkcje analizy i porównywania
export class MaterialsAnalyzer {
  /**
   * Wyszukuje materiały po kategorii
   */
  static filterByCategory(category: Material['category']): Material[] {
    return MATERIALS_DATABASE.filter(m => m.category === category);
  }

  /**
   * Wyszukuje materiały po zastosowaniu
   */
  static findByApplication(keyword: string): Material[] {
    const lowerKeyword = keyword.toLowerCase();
    return MATERIALS_DATABASE.filter(m => 
      m.applications.some(app => app.toLowerCase().includes(lowerKeyword))
    );
  }

  /**
   * Porównuje właściwości mechaniczne
   */
  static compareMechanical(...materialIds: string[]): any {
    const materials = materialIds.map(id => 
      MATERIALS_DATABASE.find(m => m.id === id)
    ).filter(Boolean);

    if (materials.length === 0) return null;

    const properties = [
      'tensileStrength',
      'tensileModulus', 
      'flexuralStrength',
      'impactStrength',
      'elongationAtBreak'
    ];

    const comparison: any = {};
    
    properties.forEach(prop => {
      comparison[prop] = materials.map(m => ({
        name: m!.name,
        value: (m!.mechanical as any)[prop]
      }));
    });

    return comparison;
  }

  /**
   * Rekomenduje materiał na podstawie wymagań
   */
  static recommendMaterial(requirements: {
    minTensileStrength?: number;
    minImpactStrength?: number;
    maxPrice?: number;
    minServiceTemp?: number;
    maxServiceTemp?: number;
    needsTransparency?: boolean;
    chemicalExposure?: ('acids' | 'bases' | 'solvents' | 'oils')[];
    outdoor?: boolean;
    foodContact?: boolean;
  }): Material[] {
    let candidates = [...MATERIALS_DATABASE];

    // Filtruj po wytrzymałości na rozciąganie
    if (requirements.minTensileStrength) {
      candidates = candidates.filter(m => 
        m.mechanical.tensileStrength >= requirements.minTensileStrength!
      );
    }

    // Filtruj po odporności na uderzenia
    if (requirements.minImpactStrength) {
      candidates = candidates.filter(m => {
        const impact = m.mechanical.impactStrength;
        return typeof impact === 'number' && impact >= requirements.minImpactStrength!;
      });
    }

    // Filtruj po cenie
    if (requirements.maxPrice) {
      candidates = candidates.filter(m => m.price <= requirements.maxPrice!);
    }

    // Filtruj po temperaturze pracy
    if (requirements.minServiceTemp) {
      candidates = candidates.filter(m => 
        m.thermal.glassTransition <= requirements.minServiceTemp!
      );
    }
    if (requirements.maxServiceTemp) {
      candidates = candidates.filter(m => 
        m.thermal.maxServiceTemp >= requirements.maxServiceTemp!
      );
    }

    // Filtruj po przezroczystości
    if (requirements.needsTransparency) {
      candidates = candidates.filter(m => m.optical.transparency > 80);
    }

    // Filtruj po odporności chemicznej
    if (requirements.chemicalExposure && requirements.chemicalExposure.length > 0) {
      candidates = candidates.filter(m => {
        return requirements.chemicalExposure!.every(chem => 
          m.chemical.chemicalResistance[chem] === 'good' || 
          m.chemical.chemicalResistance[chem] === 'excellent'
        );
      });
    }

    // Filtruj dla zastosowań zewnętrznych
    if (requirements.outdoor) {
      candidates = candidates.filter(m => 
        m.chemical.weatherResistance === 'good' || 
        m.chemical.weatherResistance === 'excellent'
      );
    }

    // Filtruj dla kontaktu z żywnością
    if (requirements.foodContact) {
      candidates = candidates.filter(m => 
        ['pmma-cast', 'pmma-extruded', 'pc', 'petg', 'hdpe', 'pp'].includes(m.id)
      );
    }

    // Sortuj po punktacji (im więcej kryteriów spełnia, tym lepiej)
    return candidates.sort((a, b) => {
      let scoreA = 0, scoreB = 0;
      
      // Punkty za właściwości mechaniczne
      if (requirements.minTensileStrength) {
        scoreA += a.mechanical.tensileStrength / requirements.minTensileStrength;
        scoreB += b.mechanical.tensileStrength / requirements.minTensileStrength;
      }
      
      // Punkty za cenę (odwrotnie - niższa = lepsza)
      if (requirements.maxPrice) {
        scoreA += (requirements.maxPrice - a.price) / requirements.maxPrice;
        scoreB += (requirements.maxPrice - b.price) / requirements.maxPrice;
      }
      
      return scoreB - scoreA;
    });
  }

  /**
   * Oblicza wskaźnik wydajności (performance index)
   */
  static calculatePerformanceIndex(
    materialId: string,
    weights: {
      strength?: number;
      stiffness?: number;
      toughness?: number;
      temperature?: number;
      cost?: number;
    } = {}
  ): number {
    const material = MATERIALS_DATABASE.find(m => m.id === materialId);
    if (!material) return 0;

    // Domyślne wagi
    const w = {
      strength: weights.strength || 0.25,
      stiffness: weights.stiffness || 0.25,
      toughness: weights.toughness || 0.25,
      temperature: weights.temperature || 0.15,
      cost: weights.cost || 0.10
    };

    // Normalizacja (0-100)
    const normalized = {
      strength: (material.mechanical.tensileStrength / 120) * 100,
      stiffness: (material.mechanical.tensileModulus / 3.5) * 100,
      toughness: Math.min((material.mechanical.impactStrength as number || 0) / 100, 1) * 100,
      temperature: (material.thermal.maxServiceTemp / 260) * 100,
      cost: ((100 - material.price) / 100) * 100
    };

    // Oblicz wskaźnik ważony
    return (
      normalized.strength * w.strength +
      normalized.stiffness * w.stiffness +
      normalized.toughness * w.toughness +
      normalized.temperature * w.temperature +
      normalized.cost * w.cost
    );
  }

  /**
   * Generuje raport porównawczy
   */
  static generateComparisonReport(materialIds: string[]): string {
    const materials = materialIds.map(id => 
      MATERIALS_DATABASE.find(m => m.id === id)
    ).filter(Boolean) as Material[];

    if (materials.length === 0) return 'Nie znaleziono materiałów';

    let report = '# RAPORT PORÓWNAWCZY MATERIAŁÓW\n\n';
    
    // Nagłówek
    report += '## Porównywane materiały:\n';
    materials.forEach(m => {
      report += `- **${m.name}** (${m.tradenames[0]})\n`;
    });
    
    // Właściwości mechaniczne
    report += '\n## Właściwości mechaniczne:\n';
    report += '| Właściwość | ' + materials.map(m => m.name).join(' | ') + ' |\n';
    report += '|------------|' + materials.map(() => '-----').join('|') + '|\n';
    
    const mechProps = [
      { key: 'tensileStrength', label: 'Wytrzymałość na rozciąganie (MPa)' },
      { key: 'tensileModulus', label: 'Moduł Younga (GPa)' },
      { key: 'impactStrength', label: 'Udarność (kJ/m²)' },
      { key: 'elongationAtBreak', label: 'Wydłużenie (%)' }
    ];
    
    mechProps.forEach(prop => {
      report += `| ${prop.label} | `;
      report += materials.map(m => (m.mechanical as any)[prop.key]).join(' | ');
      report += ' |\n';
    });
    
    // Właściwości termiczne
    report += '\n## Właściwości termiczne:\n';
    report += '| Właściwość | ' + materials.map(m => m.name).join(' | ') + ' |\n';
    report += '|------------|' + materials.map(() => '-----').join('|') + '|\n';
    report += `| Max temp. pracy (°C) | ${materials.map(m => m.thermal.maxServiceTemp).join(' | ')} |\n`;
    report += `| Temp. zeszklenia (°C) | ${materials.map(m => m.thermal.glassTransition).join(' | ')} |\n`;
    
    // Koszty
    report += '\n## Analiza kosztów:\n';
    report += '| Materiał | Cena (PLN/kg) | Gęstość (g/cm³) | Koszt objętościowy (PLN/dm³) |\n';
    report += '|----------|---------------|-----------------|------------------------------|\n';
    materials.forEach(m => {
      const volumeCost = (m.price * m.density).toFixed(2);
      report += `| ${m.name} | ${m.price} | ${m.density} | ${volumeCost} |\n`;
    });
    
    // Rekomendacje
    report += '\n## Rekomendacje:\n';
    
    // Najlepszy do zastosowań strukturalnych
    const bestStrength = materials.reduce((a, b) => 
      a.mechanical.tensileStrength > b.mechanical.tensileStrength ? a : b
    );
    report += `- **Najwyższa wytrzymałość:** ${bestStrength.name}\n`;
    
    // Najlepszy do uderzeń
    const bestImpact = materials.reduce((a, b) => {
      const aImpact = typeof a.mechanical.impactStrength === 'number' ? a.mechanical.impactStrength : 0;
      const bImpact = typeof b.mechanical.impactStrength === 'number' ? b.mechanical.impactStrength : 0;
      return aImpact > bImpact ? a : b;
    });
    report += `- **Najlepsza udarność:** ${bestImpact.name}\n`;
    
    // Najtańszy
    const cheapest = materials.reduce((a, b) => a.price < b.price ? a : b);
    report += `- **Najniższy koszt:** ${cheapest.name}\n`;
    
    // Najlepszy do wysokich temperatur
    const bestTemp = materials.reduce((a, b) => 
      a.thermal.maxServiceTemp > b.thermal.maxServiceTemp ? a : b
    );
    report += `- **Najwyższa temp. pracy:** ${bestTemp.name}\n`;
    
    return report;
  }

  /**
   * Oblicza ekwiwalent CO2 dla produkcji
   */
  static calculateCarbonFootprint(
    materialId: string,
    weight: number // kg
  ): number {
    const material = MATERIALS_DATABASE.find(m => m.id === materialId);
    if (!material) return 0;
    
    return weight * material.sustainability.carbonFootprint;
  }

  /**
   * Sprawdza kompatybilność klejenia
   */
  static checkBondingCompatibility(material1Id: string, material2Id: string): {
    compatible: boolean;
    method: string;
    notes: string;
  } {
    const m1 = MATERIALS_DATABASE.find(m => m.id === material1Id);
    const m2 = MATERIALS_DATABASE.find(m => m.id === material2Id);
    
    if (!m1 || !m2) {
      return { compatible: false, method: '', notes: 'Materiał nie znaleziony' };
    }
    
    // Zasady kompatybilności
    const compatibility: Record<string, Record<string, any>> = {
      'pmma-cast': {
        'pmma-cast': { compatible: true, method: 'Klej rozpuszczalnikowy (dichlorometan)', notes: 'Doskonałe połączenie' },
        'pmma-extruded': { compatible: true, method: 'Klej rozpuszczalnikowy', notes: 'Dobre połączenie' },
        'pc': { compatible: true, method: 'Klej dwuskładnikowy epoksydowy', notes: 'Wymaga przygotowania powierzchni' },
        'petg': { compatible: true, method: 'Klej cyjanoakrylowy', notes: 'Szybkie wiązanie' }
      },
      'pc': {
        'pc': { compatible: true, method: 'Klej rozpuszczalnikowy (MEK)', notes: 'Mocne połączenie' },
        'pmma-cast': { compatible: true, method: 'Klej dwuskładnikowy', notes: 'Średnia wytrzymałość' }
      }
      // ... można rozbudować dla wszystkich kombinacji
    };
    
    const result = compatibility[m1.id]?.[m2.id] || 
                  compatibility[m2.id]?.[m1.id] ||
                  { compatible: false, method: 'Klej strukturalny', notes: 'Sprawdź kompatybilność' };
    
    return result;
  }
}